import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import { debug } from '../utils/debug';
import type { PendingActor, User } from '../types';

interface UseAuthReturn {
  isLoading: boolean;
  isAuthenticated: boolean;
  isVerified: boolean;
  user: User | null;
  pendingActor: PendingActor | null;
  error: string | null;
  retry: () => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [pendingActor, setPendingActor] = useState<PendingActor | null>(null);
  const [error, setError] = useState<string | null>(null);

  const verifyAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      debug.log('🔐 useAuth - Verifying authentication...');

      const response = await apiClient.verifyAuth();

      if (response.success && response.authenticated) {
        if (response.data?.user) {
          debug.log('✅ useAuth - Verified user authenticated:', {
            userId: response.data.user.id,
            telegramId: response.data.user.telegram_id,
            maxId: response.data.user.max_id,
          });
          setIsAuthenticated(true);
          setIsVerified(true);
          setUser(response.data.user);
          setPendingActor(null);
          setError(null);
        } else if (response.data?.pending_actor) {
          debug.log('✅ useAuth - Pending actor authenticated:', response.data.pending_actor);
          setIsAuthenticated(true);
          setIsVerified(false);
          setUser(null);
          setPendingActor(response.data.pending_actor);
          setError(null);
        } else {
          debug.warn('❌ useAuth - Authenticated without actor payload');
          setIsAuthenticated(false);
          setIsVerified(false);
          setUser(null);
          setPendingActor(null);
          setError('Authentication failed');
        }
      } else {
        debug.warn('❌ useAuth - Authentication failed:', response.error);

        setIsAuthenticated(false);
        setIsVerified(false);
        setUser(null);
        setPendingActor(null);
        setError(response.error || 'Authentication failed');
      }
    } catch (err) {
      debug.error('❌ useAuth - Error verifying authentication:', err);

      setIsAuthenticated(false);
      setIsVerified(false);
      setUser(null);
      setPendingActor(null);
      setError('Failed to verify authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const retry = useCallback(async () => {
    debug.log('🔄 useAuth - Retrying authentication...');
    await verifyAuth();
  }, [verifyAuth]);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  return {
    isLoading,
    isAuthenticated,
    isVerified,
    user,
    pendingActor,
    error,
    retry,
  };
};

